import React, { useRef, useState } from 'react';
import { useGqlMutation, useGqlQuery } from '@API/useAvQuery';
import { usePostUserMessage, useStartAgenticSession } from '@components/Copilot/agentHooks';
import {
  END_AGENTIC_SESSION,
  EXECUTE_CUSTOM_OPERATION,
  GET_AGENT_STATUS,
  GET_FEEDBACK_CATEGORIES,
  SUBMIT_AGENT_FEEDBACK,
} from '@components/Copilot/gql';
import {
  AgentResponse,
  ChatbotEndSessionRequest,
  ChatbotErrorType,
  ChatbotStatus,
  ChatbotWfStatus,
  ChatMessage,
  ChatRunInfo,
  CopilotGetStatusChatMessageType,
  EndChatbotSessionData,
  FeedbackInput,
  GenerateChatbotSummaryData,
  GenerateChatbotSummaryRequest,
  GetChatbotMessageResponse,
  InitChatbotSessionRequest,
  InitChatbotSessionResponse,
  OperationInput,
  PublishChatbotMessageData,
  PublishChatbotMessageRequest,
  TCopilotError,
} from '@components/Copilot/types';
import { QueryKey } from '@fabric-ui/utils';
import { Query, UseMutationOptions } from '@tanstack/react-query';
import { GraphQLError } from 'graphql';
import { gql } from 'graphql-request';

export { usePostUserMessage, useStartAgenticSession };

export const useEndAgenticSession = (
  options?: Omit<UseMutationOptions<boolean, GraphQLError, { sessionId: string }>, 'variablesAdapter'>
) =>
  useGqlMutation<boolean, { sessionId: string }>({
    mutationKey: [QueryKey.Copilot, 'endAgenticSession'],
    mutation: END_AGENTIC_SESSION,
    ...options,
  });

export const useExecuteCustomOperation = (
  options?: Omit<UseMutationOptions<string, GraphQLError, { operationInput: OperationInput }>, 'variablesAdapter'>
) =>
  useGqlMutation<string, { operationInput: OperationInput }>({
    mutationKey: [QueryKey.Copilot, 'executeCustomOperation'],
    mutation: EXECUTE_CUSTOM_OPERATION,
    ...options,
  });

export interface FeedbackCategoryOption {
  key: string;
  label: string;
}

interface GetFeedbackCategoriesResponse {
  getFeedbackCategories: FeedbackCategoryOption[];
}

export const useGetFeedbackCategories = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useGqlQuery<GetFeedbackCategoriesResponse, FeedbackCategoryOption[]>({
    queryKey: [QueryKey.Copilot, 'getFeedbackCategories'],
    query: GET_FEEDBACK_CATEGORIES,
    enabled,
    staleTime: Infinity,
  });

export interface FeedbackResponse {
  success: boolean;
  loopId: string;
  isUpdate: boolean;
}

export const useSubmitAgentFeedback = (
  options?: Omit<UseMutationOptions<FeedbackResponse, GraphQLError, { feedbackInput: FeedbackInput }>, 'variablesAdapter'>
) =>
  useGqlMutation<FeedbackResponse, { feedbackInput: FeedbackInput }>({
    mutationKey: [QueryKey.Copilot, 'submitAgentFeedback'],
    mutation: SUBMIT_AGENT_FEEDBACK,
    ...options,
  });

export const useGetAgentStatus = ({
  sessionId,
  enabled = false,
  onSuccess,
  onError,
}: {
  sessionId: string;
  enabled?: boolean;
  onSuccess?: (val: AgentResponse['getAgentStatus']) => void;
  onError?: (error: GraphQLError) => void;
}) =>
  useGqlQuery<AgentResponse, AgentResponse['getAgentStatus']>({
    queryKey: [QueryKey.Copilot, 'getAgentStatus', sessionId],
    query: GET_AGENT_STATUS,
    variables: { sessionId },
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    onSuccess,
    onError,
    refetchInterval: (query: Query<AgentResponse, GraphQLError>) => {
      const status = query.state.data?.getAgentStatus?.status;
      if ([ChatbotStatus.LOADING_DATA, ChatbotStatus.TOOL_CALLING, ChatbotStatus.THINKING, ChatbotStatus.UNSPECIFIED].includes(status!)) {
        return 3000;
      }
      return false;
    },
  });

// Old hooks
const INIT_CHATBOT_SESSION = gql`
  query initChatbotSession($request: InitChatbotSessionRequest!) {
    initChatbotSession(request: $request) {
      data {
        chatRunInfo
        defaultPrompts
        useFallbackQueries
      }
      error {
        message
        type
      }
    }
  }
`;

const PUBLISH_CHATBOT_MESSAGE = gql`
  mutation publishChatbotMessage($chatRunInfo: ChatRunInfoScalar, $userPrompt: String!) {
    publishChatbotMessage(chatRunInfo: $chatRunInfo, userPrompt: $userPrompt) {
      data
      error {
        message
        type
      }
    }
  }
`;

const GENERATE_CHATBOT_SUMMARY = gql`
  mutation generateChatbotSummary($chatRunInfo: ChatRunInfoScalar) {
    generateChatbotSummary(chatRunInfo: $chatRunInfo) {
      data
      error {
        message
        type
      }
    }
  }
`;

const GET_CHATBOT_MESSAGE = gql`
  query getChatbotMessage($chatRunInfo: ChatRunInfoScalar, $messageType: ChatbotMessageType!) {
    getChatbotMessage(chatRunInfo: $chatRunInfo, messageType: $messageType) {
      data {
        message
        status
      }
      error {
        message
        type
      }
    }
  }
`;

const END_CHATBOT_SESSION = gql`
  mutation endChatbotSession($chatRunInfo: ChatRunInfoScalar) {
    endChatbotSession(chatRunInfo: $chatRunInfo) {
      data
      error {
        message
        type
      }
    }
  }
`;

export const useInitChatbotSession = ({
  enabled = false,
  variables,
  onSuccess,
}: {
  enabled?: boolean;
  variables: InitChatbotSessionRequest;
  onSuccess?: (val: InitChatbotSessionResponse['initChatbotSession']) => void;
}) =>
  useGqlQuery<InitChatbotSessionResponse, InitChatbotSessionResponse['initChatbotSession']>({
    queryKey: [QueryKey.Copilot, QueryKey.InitChatbotSession, variables?.entityKey],
    query: INIT_CHATBOT_SESSION,
    variables: { request: variables },
    enabled: enabled && !!variables,
    gcTime: 0,
    staleTime: 0,
    onSuccess,
  });

export const usePublishChatbotMessage = (
  options?: Omit<UseMutationOptions<PublishChatbotMessageData, GraphQLError, PublishChatbotMessageRequest>, 'variablesAdapter'>
) =>
  useGqlMutation<PublishChatbotMessageData, PublishChatbotMessageRequest>({
    mutationKey: [QueryKey.Copilot, QueryKey.PublishChatbotMessage],
    mutation: PUBLISH_CHATBOT_MESSAGE,
    gcTime: 0,
    ...options,
  });

export const useGenerateChatbotSummary = (
  options?: Omit<UseMutationOptions<GenerateChatbotSummaryData, GraphQLError, GenerateChatbotSummaryRequest>, 'variablesAdapter'>
) =>
  useGqlMutation<GenerateChatbotSummaryData, GenerateChatbotSummaryRequest>({
    mutationKey: [QueryKey.Copilot, QueryKey.GenerateChatbotSummary],
    mutation: GENERATE_CHATBOT_SUMMARY,
    gcTime: 0,
    ...options,
  });

export const useEndOldChatbotSession = () =>
  useGqlMutation<EndChatbotSessionData, ChatbotEndSessionRequest>({
    mutationKey: [QueryKey.Copilot, QueryKey.EndChatbotSession],
    mutation: END_CHATBOT_SESSION,
  });

export const useGetChatbotMessage = ({
  chatRunInfo,
  messageType,
  enabled = false,
  onSuccess,
  onError,
}: {
  chatRunInfo: ChatRunInfo;
  messageType: CopilotGetStatusChatMessageType;
  enabled?: boolean;
  onSuccess?: (val: GetChatbotMessageResponse['getChatbotMessage']) => void;
  onError?: (error: GraphQLError) => void;
}) =>
  useGqlQuery<GetChatbotMessageResponse, GetChatbotMessageResponse['getChatbotMessage']>({
    queryKey: [QueryKey.Copilot, QueryKey.GetChatbotMessage, chatRunInfo.threadId],
    query: GET_CHATBOT_MESSAGE,
    variables: { chatRunInfo, messageType },
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    onSuccess,
    onError,
    refetchInterval: 3000,
  });

export const useChatbotConversation = ({
  isCopilotOpen = false,
  chatRunInfo,
  addMessage,
  setErrorMessage,
}: {
  isCopilotOpen: boolean;
  chatRunInfo: ChatRunInfo;
  addMessage: (message: ChatMessage) => void;
  setErrorMessage: React.Dispatch<React.SetStateAction<TCopilotError | null>>;
}) => {
  const onSummarySuccessRef = useRef<((data: { generateChatbotSummary: string }) => void) | null>(null);
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [messageTypeState, setMessageTypeState] = useState(CopilotGetStatusChatMessageType.ANSWER);

  if (!isCopilotOpen && isPollingEnabled) {
    setIsPollingEnabled(false);
  }

  const onError = () => {
    setErrorMessage({
      message: 'Something went wrong. Please try again.',
      type: ChatbotErrorType.INTERNAL,
    });
    setIsPollingEnabled(false);
  };

  const { mutate: publishMessage, isPending } = usePublishChatbotMessage({
    onSuccess: () => {
      setIsPollingEnabled(true);
      setMessageTypeState(CopilotGetStatusChatMessageType.ANSWER);
    },
    onError,
  });

  const { mutate: triggerSummarize, isPending: isSummarizing } = useGenerateChatbotSummary({
    onSuccess: () => {
      setIsPollingEnabled(true);
      setMessageTypeState(CopilotGetStatusChatMessageType.SUMMARY);
    },
    onError,
  });

  const summarizeMessage = (variables: GenerateChatbotSummaryRequest, options?: { onSuccess: (data: any) => void }) => {
    if (options?.onSuccess) {
      onSummarySuccessRef.current = options.onSuccess;
    }
    triggerSummarize(variables);
  };

  useGetChatbotMessage({
    chatRunInfo,
    messageType: messageTypeState,
    enabled: isPollingEnabled && isCopilotOpen && !!chatRunInfo.threadId && !!messageTypeState,
    onSuccess: ({ data, error }) => {
      if (error) {
        setErrorMessage(error);
        setIsPollingEnabled(false);
      } else if (data?.status === ChatbotWfStatus.CHATBOT_READY && Object.keys(data.message).length) {
        addMessage(data.message);
        setErrorMessage(null);
        setIsPollingEnabled(false);
        if (messageTypeState === CopilotGetStatusChatMessageType.SUMMARY && onSummarySuccessRef.current) {
          onSummarySuccessRef.current({ generateChatbotSummary: data.message.content });
          onSummarySuccessRef.current = null;
        }
      }
    },
    onError,
  });

  return {
    publishMessage,
    summarizeMessage,
    isSummarizing: isSummarizing || (isPollingEnabled && messageTypeState === CopilotGetStatusChatMessageType.SUMMARY),
    isPollingEnabled: isSummarizing || isPending || isPollingEnabled,
  };
};
